"use client";

import {type JSX, useState} from "react";

import Link from "next/link";

import SmartImage from "@/shared/ui/smart-image";

import {useTranslation} from "@/shared/i18n/use-translation";
import {PhoneNumber} from "@/shared/ui/data-display/phone-number/phone-number";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {assetPath} from "@/shared/utils/asset-path";

import "./styles/franchise-contact-grid.scss";

export interface DestinationBoxData {
    id: string;
    title: string;
    url: string;
    phones: Array<{label: string; number: string}>;
}

export interface FranchiseContactGridProps {
    destinations: Array<DestinationBoxData>;
}

export function FranchiseContactGrid({destinations}: FranchiseContactGridProps): JSX.Element {
    const [query, setQuery] = useState("");

    const filtered = query.trim() ? destinations.filter((d) => d.title.toLowerCase().includes(query.trim().toLowerCase())) : destinations;
    const {t} = useTranslation("search");

    return (
        <section className="banner-form-destinations">
            <div className="title-black">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path
                        d="M12 21C12 21 5 14.25 5 9C5 5.13401 8.13401 2 12 2C15.866 2 19 5.13401 19 9C19 14.25 12 21 12 21Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <circle cx="12" cy="9" r="3" stroke="currentColor" strokeWidth="1.5" />
                </svg>
                {t("chooseCity")}
            </div>
            <ContainerBase>
                <div className="banner-form-destinations__search">
                    <input
                        type="text"
                        className="banner-form-destinations__search-input"
                        placeholder={t("locationPlaceholder")}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </div>
                {filtered.length > 0 ? (
                    <div className="banner-form-destinations__grid">
                        {filtered.map((destination) => (
                            <div key={destination.id} className="destination-box">
                                <div className="destination-box__wrap">
                                    <h4 className="destination-box__title">{destination.title}</h4>
                                    {destination.phones.length > 0 && (
                                        <div className="destination-box__phones">
                                            {destination.phones.map((phone, i) => (
                                                <PhoneNumber
                                                    key={i}
                                                    number={phone.number}
                                                    label={phone.label}
                                                    showLabel={Boolean(phone.label)}
                                                    size={14}
                                                    iconSize={16}
                                                    className="destination-box__phone-item"
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <Link className="destination-box__location" href={destination.url}>
                                    VIEW LOCATION{" "}
                                    <span>
                                        <SmartImage src={assetPath("/images/icons/arrow-right.svg")} alt="" width={16} height={16} />
                                    </span>
                                </Link>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="banner-form-destinations__empty">No destinations found.</p>
                )}
            </ContainerBase>
        </section>
    );
}
